import { POSTHOG_HOST, POSTHOG_KEY } from '@/lib/constants';
import { useAppState } from '@/lib/hooks/use-app-state';
import { UserType } from '@/lib/types';
import { getFullName } from '@/utils/name';
import posthog from 'posthog-js';
import { PostHogProvider as PostHogProviderFromPosthog } from 'posthog-js/react';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useState } from 'react';

export const PosthogProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { platoUser, superAdmin, primaryUserType } = useAppState();
    const [posthogIsAlreadySetup, setPosthogIsAlreadySetup] = useState(false);

    const initPostHog = () => {
        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST || 'https://app.posthog.com',
            // Enable debug mode in development
            loaded: (posthog) => {
                if (process.env.NODE_ENV === 'development') posthog.debug();
            },
            session_recording: {
                maskAllInputs: false,
                maskInputOptions: {
                    password: true,
                },
            },
        });
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (process.env.NODE_ENV === 'development') return;
        if (posthogIsAlreadySetup) return;

        if (primaryUserType === UserType.PlatoUser && platoUser && !superAdmin) {
            initPostHog();

            posthog.identify(platoUser.email, {
                IS_SUPER_ADMIN: false,
                id: platoUser.id,
                firstName: platoUser.firstName,
                lastName: platoUser.lastName,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                customer_slug: platoUser.platoCustomer.slug,
                erpSalesRepId: platoUser.erpSalesRepId,
                salesRepIds: platoUser.salesRepIds,
                showDataForErpSalesRepId: platoUser.showDataForErpSalesRepId,
                email: platoUser.email,
                roles: platoUser.roles.join(','),
                description: platoUser.descriptionString,
            });

            posthog.register({
                touch:
                    'ontouchstart' in window ||
                    navigator.maxTouchPoints > 0 ||
                    (navigator as unknown as { msMaxTouchPoints: number }).msMaxTouchPoints > 0,
            });

            posthog.capture('$set', {
                $set_once: { first_seen_date: new Date().toISOString() },
            });

            posthog.group('plato_customer', `id:${platoUser.platoCustomer.id}`, {
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                customer_slug: platoUser.platoCustomer.slug,
            });

            setPosthogIsAlreadySetup(true);
        } else if (primaryUserType === UserType.SuperAdmin && superAdmin) {
            initPostHog();

            posthog.identify(superAdmin.email, {
                IS_SUPER_ADMIN: true,
                id: superAdmin.id,
                firstName: superAdmin.firstName,
                lastName: superAdmin.lastName,
                name: getFullName(superAdmin.firstName, superAdmin.lastName),
                email: superAdmin.email,
            });

            posthog.group('plato_customer', 'id:plato_team_superuser', {
                customer_id: 'Plato Team',
                customer_name: 'Plato Team',
            });

            setPosthogIsAlreadySetup(true);
        }
    }, [platoUser, superAdmin, primaryUserType, posthogIsAlreadySetup]);

    return <PostHogProviderFromPosthog client={posthog}>{children}</PostHogProviderFromPosthog>;
};
